import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Typography } from '@mui/material';

import { people } from '../tagging/tagging';
import { describeManageError } from '../manage/manage';
import { DeleteDialog } from '../manage/ManageParts';
import { kindLabel, ManagedList } from './listManager';
import { CRM_DELETE_LIST } from './lists.queries';

interface DeleteListDialogProps {
  list: ManagedList;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * Deletes a list, or a saved filter. Its people stay in the CRM; only their
 * membership goes. The API refuses while a campaign still to send or a workflow
 * still running uses it. A list with members needs its name typed.
 */
export const DeleteListDialog = ({ list, onClose, onDeleted }: DeleteListDialogProps) => {
  const [refusal, setRefusal] = useState<unknown>();
  const [remove, { loading }] = useMutation(CRM_DELETE_LIST);
  const isList = list.type === 'STATIC';
  const members = isList ? list.contactCount : 0;

  const run = async () => {
    setRefusal(undefined);
    try {
      await remove({ variables: { id: list.id } });
      onDeleted();
    } catch (err) {
      setRefusal(err);
    }
  };

  return (
    <DeleteDialog
      title={`Delete ${list.name}?`}
      confirm={`Delete ${kindLabel(list).toLowerCase()}`}
      typeToConfirm={members ? list.name : undefined}
      error={refusal === undefined ? undefined : describeManageError(refusal, 'list')}
      running={loading}
      onConfirm={run}
      onClose={onClose}
    >
      {isList ? (
        <Typography variant="body2">
          {members ? (
            <>
              The <strong>{people(members)}</strong> on it stay in the CRM: only the list, and their place on it, go.
            </>
          ) : (
            'Nobody is on it, so no contact changes.'
          )}
        </Typography>
      ) : (
        <Typography variant="body2">
          A saved filter holds nobody: only the filter goes, and no contact changes.
        </Typography>
      )}
      <Typography variant="body2">
        Campaigns already sent to it keep their figures. While a campaign still to send or a workflow still running uses
        it, it cannot be deleted.
      </Typography>
      {members > 0 && (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
          This cannot be undone: the list cannot be rebuilt as it was.
        </Typography>
      )}
    </DeleteDialog>
  );
};
